import { format } from 'date-fns';
import { transformJob } from 'helpers';
import { applySnapshot, clone, detach, flow, getSnapshot, types as t } from 'mobx-state-tree';

import { isSameEditorState, isEditorHtmlEmpty } from '@utils/draftjs';

import { Job } from 'models/jobs';
import { Language } from 'models/ui';
import { withEnv } from 'models/withEnv';

export const shortDescriptionMaxLength = 340;

export const getHtmlContentLength = (html) => {
  const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
  const content = parsedHtml.body.textContent?.trim() ?? '';
  return content.length;
};

const isShortDescriptionUnderMaxLength = (shortDescriptionHtml) =>
  getHtmlContentLength(shortDescriptionHtml) <= shortDescriptionMaxLength;

const JobEditErrors = t.model('JobEditErrors', {
  language: t.identifier,
  location: false,
  title: false,
  shortDescription: false,
});

const JobStoreRef = t.maybe(
  t.reference(Job, {
    get(identifier, parent) {
      return parent.env.jobStore.items.get(identifier);
    },
    set(value) {
      return value.id;
    },
  }),
);

const Edit = t
  .model('JobEditModel', {
    currentLanguage: t.maybe(Language),
    available: t.array(Language),
    saving: false,
    editorLoaded: false,
    original: JobStoreRef,
    errors: t.map(JobEditErrors),
    showLanguageErrors: false,
  })
  .views(withEnv)
  .actions((self) => {
    let initialState;
    return {
      goBackOneStep: (currentStep) => {
        switch (currentStep) {
          case 2:
            return self.goToEditForm();
          case 3:
            return self.goToEditRewards();
          case 4:
            return self.goToEditAccess();
          case 5:
            return self.goToEditPreview();
          case 1:
          default:
            return self.goToEdit();
        }
      },
      setInitialState: () => (initialState = getSnapshot(self)),
      resetState: () => {
        applySnapshot(self, {
          ...initialState,
          currentLanguage: self.env.current.info.defaultLanguage,
        });
      },
      saveDraftStage: (stage) => {
        if (self.draftStage >= stage) {
          return;
        }

        const draftStage = Math.max(self.draftStage, stage);
        self.env.api.jobs.edit(self.id, { draftStage }).then((res) => {
          if (!res.error) {
            self.localEdit('draftStage', stage);
          }
        });
      },
      removeLanguageLocal: (language) => {
        const langsWithoutDeleted = self.languages.filter((obj) => obj.language !== language);
        if (langsWithoutDeleted.length >= 1) {
          self.selectLanguage(langsWithoutDeleted[0].language);
        } else {
          self.selectLanguage(undefined);
        }
        self.removeLanguage(language);
        if (self.original) {
          self.original.removeLanguage(language);
        }
      },
      deleteLanguage: flow(function* (language) {
        const isLocal = self.languages.find((obj) => obj.language === language).isLocal;
        if (isLocal) {
          self.removeLanguageLocal(language);
          return;
        }
        const res = yield self.env.api.jobs.deleteLanguage(self.id, language);
        if (res.error) {
          self.env.showError(self.env.t('job.create.languages.delete.error'), {
            subtitle: res.data,
          });
        } else {
          self.env.showSuccess(self.env.t('job.create.languages.delete.success'));
          self.removeLanguageLocal(language);
        }
      }),
      selectLanguage: (lang) => (self.currentLanguage = lang),
      setAvailable: (langs) => (self.available = langs.split(',').map((key) => key.trim())),
      saveEdit: (step, cont) => {
        self[`saveStep${step}`](cont);
      },
      afterCreate: () => {
        if (self.languages.length > 0) {
          self.currentLanguage = self.languages[0].language;
        } else {
          self.addLanguage(self.env.current.info.defaultLanguage);
          self.currentLanguage = self.env.current.info.defaultLanguage;
        }
        self.languages.forEach((lang) => self.errors.put({ language: lang.language }));
        if (
          !self.department &&
          !self.editing &&
          self.env.current.whoami.departmentIds.length === 1
        ) {
          self.department = self.env.current.whoami.departmentIds[0];
        }
        self.setInitialState();
      },
      enableLanguage: flow(function* (lang) {
        const obj = self.languages.find((obj) => obj.language === lang);

        if (self.isPublished && !obj.isComplete) {
          self.notifyIncompletes(lang);
          return;
        }
        self.saving = true;
        const res = yield self.env.api.jobs.edit(self.id, {
          languages: [{ language: lang, isEnabled: true }],
        });

        if (!res.error) {
          self.env.showSuccess(self.env.t('job.create.languages.publish.success'), {
            subtitle: obj.display,
          });
          const og = self.original.languages.find((obj) => obj.language === lang);
          if (og) og.enable();
          obj.enable();
        } else {
          self.env.showError(self.env.t('job.create.languages.publish.error'), {
            subtitle: obj.display,
          });
        }
        self.saving = false;
      }),
      disableLanguage: flow(function* (lang) {
        const obj = self.languages.find((obj) => obj.language === lang);
        const others = self.languages.filter((obj) => obj.language !== lang);
        const someOtherPublished = others.some((lang) => lang.isEnabled && !lang.isLocal);

        if (self.isPublished && (self.languages.length < 2 || !someOtherPublished)) {
          self.env.showError(self.env.t('job.create.languages.unpublish.onlyone'), {
            subtitle: obj.display,
          });
          return;
        }

        self.saving = true;
        const res = yield self.env.api.jobs.edit(self.id, {
          languages: [{ language: lang, isEnabled: false }],
        });

        if (!res.error) {
          self.env.showSuccess(self.env.t('job.create.languages.unpublish.success'), {
            subtitle: obj.display,
          });
          const og = self.original.languages.find((obj) => obj.language === lang);
          if (og) og.disable();
          obj.disable();
        } else {
          self.env.showError(self.env.t('job.create.languages.unpublish.error'), {
            subtitle: obj.display,
          });
        }
        self.saving = false;
      }),
      removeLanguage: (language) => {
        const langsWithoutDeleted = self.languages.filter((obj) => obj.language !== language);
        const node = self.languages.find((obj) => obj.language === language);
        if (node) {
          detach(node);
          self.languages = langsWithoutDeleted;
        }
        self.errors.delete(language);
      },
      addLanguage(lang) {
        if (self.langIds.includes(lang)) return;
        if (self.languages.length > 0) {
          let existingForm = getSnapshot(self.languages[0].form);
          self.languages.push({
            description: '',
            isLocal: true,
            language: lang,
            form: {
              id: existingForm.id,
              sections: existingForm.sections.map((section) => ({
                ...section,
                questions: section.questions.map((question) => ({
                  ...question,
                  title: '',
                  options: question.options
                    ? question.options.map((option) => ({ ...option, display: '' }))
                    : [],
                })),
              })),
            },
          });
        } else {
          self.languages.push({ language: lang, isLocal: true });
        }
      },
    };
  })
  .views((self) => ({
    get languagesWithErrors() {
      let langs = [];
      self.languages.forEach((lang) => {
        const errors = self.errors.get(lang.language);
        if (!errors) return;
        if (Object.keys(errors).some((key) => errors[key] === true)) {
          langs.push(lang.language);
        }
      });
      return langs;
    },
    get currentLanguageErrors() {
      return self.errors.get(self.currentLanguage) || {};
    },
    get title() {
      return self.selectedLanguageData.title;
    },
    get shortDescription() {
      return self.selectedLanguageData.shortDescriptionHtml;
    },
    get description() {
      return self.selectedLanguageData.description;
    },
    get editing() {
      return self.id !== -1;
    },
    get selectedLanguageData() {
      const lang = self.languages.find((data) => data.language === self.currentLanguage);
      return lang || self.env.current.info.defaultLanguage;
    },
    get oldLanguageData() {
      return self.original.languages.find((data) => data.language === self.currentLanguage);
    },
    get hasErrors() {
      let hasErrors = false;
      for (let error of self.errors.values()) {
        if (Object.keys(error).some((key) => error[key] === true)) {
          hasErrors = true;
        }
      }
      return hasErrors;
    },
    get dirtyDeadline() {
      if (self.original) {
        return self.deadline !== self.original.deadline;
      }
      return self.deadline !== null;
    },
    get dirtyJobType() {
      if (self.original) {
        return self.jobType !== self.original.jobType;
      }
      return self.jobType !== 'employee';
    },
    get dirtyJobLocation() {
      if (self.original) {
        if (self.location && self.original.location) {
          return self.location.id !== self.original.location.id;
        }
        return true;
      }
      return Boolean(self.location);
    },
    get dirtyJobDepartment() {
      if (self.original) {
        return self.department !== self.original.department;
      }
      return Boolean(self.department);
    },
    get dirtyJobStatus() {
      if (self.original) {
        return self.status !== self.original.status;
      }
      return self.status !== 'full_time';
    },
    get dirtyVisibility() {
      if (self.original) {
        return self.visibility !== self.original.visibility;
      }
      return self.visibility !== 'public';
    },
    get otherLanguageData() {
      return self.languages.filter((obj) => obj.language !== self.currentLanguage);
    },

    get jobDiff() {
      const step1Fields = {
        includeCoverLetter: self.includeCoverLetter,
        includeCv: self.includeCv,
        includePhone: self.includePhone,
        includeSsn: self.includeSsn,
        requireCoverLetter: self.requireCoverLetter,
        requireCv: self.requireCv,
        requirePhone: self.requirePhone,
        requireSex: self.requireSex,
        requireSsn: self.requireSsn,
      };

      const d = {
        ...Object.keys(step1Fields)
          .filter((k) => self[k] !== self.original[k])
          .reduce((obj, key) => {
            obj[key] = self[key];
            return obj;
          }, {}),
        languages: [],
      };
      self.languages.forEach(({ isLocal, ...obj }) => {
        if (self.original) {
          const old = self.original.languages.find((oldObj) => oldObj.language === obj.language);
          if (!old) {
            d.languages.push(obj);
          } else {
            let lang = { language: obj.language };
            if (old.title !== obj.title) lang.title = obj.title;
            if (old.location !== obj.location) lang.location = obj.location;
            if (!isSameEditorState(old.shortDescriptionHtml, obj.shortDescriptionHtml)) {
              lang.shortDescriptionHtml = obj.shortDescriptionHtml;
            }
            if (!isSameEditorState(old.description, obj.description)) {
              lang.description = obj.description;
            }

            if (JSON.stringify(old.form) !== JSON.stringify(obj.form)) {
              lang.form = obj.form;
            }

            if (Object.keys(lang).length > 1) {
              d.languages.push(lang);
            }
          }
        } else {
          let lang = { language: obj.language, form: obj.form };
          if (obj.title) lang.title = obj.title;
          if (obj.location) lang.location = obj.location;
          if (!obj.descriptionEmpty) lang.description = obj.description;
          if (!obj.shortDescriptionEmpty) lang.shortDescriptionHtml = obj.shortDescriptionHtml;

          if (Object.keys(lang).length > 1) {
            d.languages.push(lang);
          }
        }
      });

      if (d.languages.length === 0) {
        delete d.languages;
      }

      if (self.dirtyDeadline) {
        d.deadline = self.deadline;
      }
      if (self.dirtyJobStatus) {
        d.status = self.status;
      }
      if (self.dirtyJobLocation) {
        d.location = self.location;
      }
      if (self.dirtyJobDepartment) {
        d.department = self.department;
      }
      if (self.dirtyJobType) {
        d.jobType = self.jobType;
      }
      if (self.dirtyVisibility) {
        d.visibility = self.visibility;
      }

      return d;
    },
  }));

const JobEditStep0 = t
  .model('JobEditStep0', {})
  .actions((self) => ({
    clearErrorForField(field) {
      const errorObj = self.errors.get(self.currentLanguage);
      if (!errorObj) {
        self.errors.put({ language: self.currentLanguage });
      } else {
        errorObj[field] = false;
        self.errors.put(errorObj);
      }
    },
    setEditorLoaded() {
      self.editorLoaded = true;
    },
    setStatus(newStatus) {
      self.status = newStatus;
    },
    setVisibility(newVisibility) {
      self.visibility = newVisibility;
    },
    setLocation(newLocation) {
      if (newLocation !== '') {
        self.clearErrorForField('location');
      }
      const location = self.env.current.info.locations.find((l) => l.id === newLocation);
      self.location = location ? clone(location, false) : null;
    },
    setDepartment(newDepartment) {
      self.department = newDepartment || null;
    },
    setJobType(newType) {
      self.jobType = newType;
    },
    setShortDescription(newShortDescription) {
      if (!isEditorHtmlEmpty(newShortDescription)) {
        self.clearErrorForField('shortDescriptionRequired');
      }
      if (isShortDescriptionUnderMaxLength(newShortDescription)) {
        self.clearErrorForField('shortDescriptionMaxLength');
      }
      self.selectedLanguageData.setShortDescription(newShortDescription);
    },
    setTitle(newTitle) {
      if (newTitle !== '') {
        self.clearErrorForField('title');
      }
      self.selectedLanguageData.setTitle(newTitle);
    },
    clearDeadline() {
      self.deadline = null;
    },
    setDeadline(deadline) {
      self.deadline = format(new Date(deadline), 'yyyy-MM-dd');
    },
    setDescription(description) {
      self.selectedLanguageData.setDescription(description);
    },
    create: flow(function* (cont) {
      const data = {
        jobType: self.jobType,
        status: self.status,
        title: self.title,
        location: self.location.id,
        department: self.department,
        languages: getSnapshot(self.languages),
        deadline: self.deadline,
        visibility: self.visibility,
        draftStage: 1,
      };

      const res = yield self.env.api.jobs.create(data);
      if (!res.error) {
        self.env.jobStore.addOrUpdate(transformJob(res.data));
        self.env.showSuccess(self.env.t('job.create.success'));
        if (cont) {
          self.env.router.replace(`/edit/${res.data.id}/form`);
        } else {
          self.env.router.replace(`/edit/${res.data.id}`);
        }
      } else {
        self.env.showError(self.env.t('job.create.error'));
      }
    }),
    saveStep0: flow(function* (cont) {
      if (!self.step0Dirty) {
        self.saveDraftStage(1);
        cont && self.goToEditForm();
        return;
      }

      if (!self.validateStep0()) {
        return;
      }

      self.saving = true;

      if (self.id === -1) {
        yield self.create(cont);
        self.saving = false;
        return;
      }

      let obj = { ...self.step1Fields, ...self.jobDiff };
      obj.draftStage = Math.max(self.draftStage, 1);
      const res = yield self.env.api.jobs.edit(self.id, {
        ...obj,
        ...(obj.location && { location: obj.location.id }),
      });
      if (!res.error) {
        self.setInitialState();
        self.env.showSuccess(self.env.t('job.update.description.success'), {
          subtitle: self.title,
        });
        self.updateLocal(res.data);
        self.original.updateLocal(res.data);
        cont && self.goToEditForm();
      } else {
        self.env.showError(self.env.t('job.update.description.error'), { subtitle: self.title });
      }
      self.saving = false;
    }),
    validateStep0() {
      self.languages.forEach((obj) => {
        let errorObj = self.errors.get(obj.language);
        if (!errorObj) {
          self.errors.put({ language: obj.language });
          errorObj = self.errors.get(obj.language);
        }

        if (!obj.title) {
          errorObj.title = true;
        }
        if (!self.location) {
          errorObj.location = true;
        }
        if (isEditorHtmlEmpty(obj.shortDescriptionHtml)) {
          errorObj.shortDescriptionRequired = true;
        } else if (!isShortDescriptionUnderMaxLength(obj.shortDescriptionHtml)) {
          errorObj.shortDescriptionMaxLength = true;
        }
        self.errors.put(errorObj);
      });
      return !self.hasErrors;
    },
  }))
  .views((self) => ({
    get step0Diff() {
      let d = {};
      if (self.original) {
        self.languages.forEach(({ isLocal, ...obj }) => {
          const old = self.original.languages.find((oldObj) => oldObj.language === obj.language);
          if (!old) {
            if (d.languages === undefined) d.languages = [];
            d.languages.push(obj);
            return;
          }
          let lang = { language: obj.language };
          if (old.title !== obj.title) lang.title = obj.title;
          if (old.location !== obj.location) lang.location = obj.location;
          if (!isSameEditorState(old.shortDescriptionHtml, obj.shortDescriptionHtml)) {
            lang.shortDescriptionHtml = obj.shortDescriptionHtml;
          }
          if (!isSameEditorState(old.description, obj.description)) {
            lang.description = obj.description;
          }
          if (Object.keys(lang).length > 1) {
            if (d.languages === undefined) d.languages = [];
            d.languages.push(lang);
          }
        });
      } else {
        self.languages.forEach((obj) => {
          let lang = { language: obj.language };
          if (obj.title) lang.title = obj.title;
          if (!obj.descriptionEmpty) lang.description = obj.description;
          if (!obj.shortDescriptionEmpty) lang.shortDescriptionHtml = obj.shortDescriptionHtml;

          if (Object.keys(lang).length > 1) {
            if (d.languages === undefined) d.languages = [];
            d.languages.push(lang);
          }
        });
      }

      if (self.dirtyDeadline) {
        d.deadline = self.deadline;
      }
      if (self.dirtyJobStatus) {
        d.status = self.status;
      }
      if (self.dirtyJobLocation) {
        d.location = self.location;
      }
      if (self.dirtyJobDepartment) {
        d.department = self.department;
      }
      if (self.dirtyJobType) {
        d.jobType = self.jobType;
      }
      if (self.dirtyVisibility) {
        d.visibility = self.visibility;
      }

      return d;
    },

    get canContinueFromStep0() {
      if (self.languages.length > 0) {
        return self.languages.some((obj) => {
          const hasShortDesciption = !isEditorHtmlEmpty(obj.shortDescriptionHtml);
          const hasCorrectLengthShortDescription = isShortDescriptionUnderMaxLength(
            obj.shortDescriptionHtml,
          );
          const hasTitle = Boolean(obj.title);
          return hasTitle || hasShortDesciption || hasCorrectLengthShortDescription;
        });
      }
      return false;
    },
    get step0Dirty() {
      return Object.keys(self.step0Diff).length > 0;
    },
  }));

const ensureRequiredAlsoIncluded = (data) => {
  Object.keys(data).forEach((key) => {
    if (key.startsWith('require') && data[key]) {
      data[key.replace('require', 'include')] = true;
    }
  });
};

const JobEditStep1 = t
  .model('JobEditStep1', {})
  .actions((self) => ({
    saveStep1: flow(function* (cont) {
      if (!self.validateStep0()) {
        self.languages.forEach((obj) => {
          if (obj.isEnabled && !obj.isComplete) {
            self.notifyIncompletes(obj.language);
          }
        });
        return;
      }
      if (!self.step1Dirty) {
        self.saveDraftStage(2);
        cont && self.goToEditRewards();
        return;
      }
      self.saving = true;
      if (self.isPublished) {
        let submit = true;
        self.languages.forEach((obj) => {
          if (obj.isEnabled && !obj.isComplete) {
            self.notifyIncompletes(obj.language);
            submit = false;
          }
        });
        if (!submit) {
          self.saving = false;
          return;
        }
      }

      let obj = self.draftStage === 1 ? { ...self.step1Fields, ...self.step1Diff } : self.step1Diff;
      ensureRequiredAlsoIncluded(obj);
      obj.draftStage = Math.max(self.draftStage, 2);
      if (obj.location) {
        obj.location = obj.location.id;
      }
      const res = yield self.env.api.jobs.edit(self.id, obj);
      if (!res.error) {
        self.env.showSuccess(self.env.t('job.update.form.success'), { subtitle: self.title });
        self.updateLocal(res.data);
        self.original.updateLocal(res.data);
        self.setInitialState();
        cont && self.goToEditRewards();
      } else {
        self.env.showError(self.env.t('job.update.form.error'), { subtitle: self.title });
      }
      self.saving = false;
    }),
    actionForQuestion(id, actionFn) {
      self.languages.forEach((obj) => {
        obj.form.sections.forEach((section) => {
          section.questions.forEach((question) => {
            if (question.id === id) {
              actionFn(question);
            }
          });
        });
      });
    },
    setSwitchValue(field, val) {
      switch (val) {
        case 'required':
          self[`require${field}`] = true;
          self[`include${field}`] = true;
          break;
        case 'optional':
          self[`require${field}`] = false;
          self[`include${field}`] = true;
          break;
        case 'off':
          self[`require${field}`] = false;
          self[`include${field}`] = false;
          break;
        default:
          throw new Error();
      }
    },
    removeQuestion(id) {
      self.actionForQuestion(id, (question) => question.remove());
    },
    moveQuestionDown(id) {
      self.actionForQuestion(id, (question) => question.moveDown());
    },
    moveQuestionUp(id) {
      self.actionForQuestion(id, (question) => question.moveUp());
    },
    setQuestionSwitch(id, value) {
      self.actionForQuestion(id, (question) => question.setSwitch(value));
    },
  }))
  .views((self) => ({
    get step1FormDiff() {
      let d = { languages: [] };

      self.languages.forEach((obj) => {
        if (!self.original) {
          d.languages.push({ language: obj.language, form: obj.form });
        } else {
          const oldLang = self.original.languages.find(
            (oldObj) => oldObj.language === obj.language,
          );
          if (!oldLang) {
            d.languages.push({ language: obj.language, form: obj.form });
          } else {
            if (JSON.stringify(oldLang.form) !== JSON.stringify(obj.form)) {
              d.languages.push({ language: obj.language, form: obj.form });
            }
          }
        }
      });

      if (d.languages.length === 0) {
        delete d.languages;
      }

      return d;
    },
    get step1Diff() {
      return {
        ...Object.keys(self.step1Fields)
          .filter((k) => self[k] !== self.original[k])
          .reduce((obj, key) => {
            obj[key] = self[key];
            return obj;
          }, {}),
        ...self.step1FormDiff,
      };
    },
    get step1Dirty() {
      return Object.keys(self.step1Diff).length > 0;
    },
    get step1Fields() {
      return {
        includeCoverLetter: self.includeCoverLetter,
        includeCv: self.includeCv,
        includePhone: self.includePhone,
        includeSsn: self.includeSsn,
        requireCoverLetter: self.requireCoverLetter,
        requireCv: self.requireCv,
        requirePhone: self.requirePhone,
        requireSex: self.requireSex,
        requireSsn: self.requireSsn,
      };
    },
  }));

const JobEditStep2 = t
  .model('JobEditStep2', {
    rewardsTitleError: false,
    rewardsError: false,
  })
  .actions((self) => ({
    saveStep2: flow(function* (cont) {
      if (!self.step2Dirty) {
        self.saveDraftStage(3);
        cont && self.goToEditAccess();
        return;
      }

      if (self.step2Valid) {
        const success = yield self.saveRewards();
        if (success) {
          self.setInitialState();
          cont && self.goToEditAccess();
        }
      } else {
        self.setRewardErrors();
      }
    }),
    setRewardErrors() {
      if (!self.hasRewards) return;
      self.rewardsTitleError = !self.rewardsTitle;
      self.rewardsError = !self.rewards;
    },
    setHasRewards(hasRewards) {
      if (!hasRewards) {
        self.rewardsError = false;
        self.rewardsTitleError = false;
      }
      self.hasRewards = hasRewards;
    },
    setRewardsTitle(rewardsTitle) {
      self.rewardsTitleError = false;
      self.rewardsTitle = rewardsTitle;
    },
    setRewardsText(rewardsText) {
      self.rewardsError = false;
      self.rewards = rewardsText;
    },
    saveRewards: flow(function* () {
      let formData = new FormData();
      self.saving = true;
      let success = true;

      const social = self.socialDiff;
      if (Object.keys(social).length > 0) {
        Object.keys(social).forEach((k) => {
          // Image is just the base64 url for preview
          // imageFile tells us if its actually changed
          if (k === 'image') return;
          if (k === 'imageFile') {
            formData.set('image', social[k]);
          } else {
            formData.set(k, social[k]);
          }
        });
        const socialRes = yield self.env.api.jobs.editSocial(self.id, formData);
        if (!socialRes.error) {
          self.social.resetAfterSave();
        } else {
          if (socialRes.data.image) {
            self.env.showError(socialRes.data.image);
          } else {
            self.env.showError(self.env.t('job.update.social.error'), { subtitle: self.title });
          }
          success = false;
        }
      }
      const rewards = self.rewardsDiff;
      if (Object.keys(rewards).length > 0) {
        const rewardsRes = yield self.env.api.jobs.editRewards(self.id, rewards);
        if (!rewardsRes.error) {
          self.original.updateLocal(rewards);
        } else {
          self.env.showError(self.env.t('job.update.rewards.error'), { subtitle: self.title });
          success = false;
        }
      }
      self.saving = false;
      if (success) {
        if (self.draftStage < 3) {
          self.env.api.jobs.edit(self.id, { draftStage: 3 });
          self.draftStage = 3;
        }
        self.env.showSuccess(self.env.t('job.update.rewards.success'), { subtitle: self.title });
        return true;
      } else {
        return false;
      }
    }),
  }))
  .views((self) => ({
    get step2Valid() {
      if (!self.hasRewards) return true;
      return !!(self.rewardsTitle && self.rewards);
    },
    get oldSocialValues() {
      return { ...self.original.social };
    },
    get socialValues() {
      return { ...self.social };
    },
    get oldRewardValues() {
      return {
        rewards: self.original.rewards,
        rewardsTitle: self.original.rewardsTitle,
        hasRewards: self.original.hasRewards,
      };
    },
    get rewardValues() {
      return {
        rewards: self.rewards,
        rewardsTitle: self.rewardsTitle,
        hasRewards: self.hasRewards,
      };
    },
    get step2Dirty() {
      return Object.keys(self.rewardsDiff).length > 0 || Object.keys(self.socialDiff).length > 0;
    },
    get socialDiff() {
      const old = self.oldSocialValues;
      const now = self.socialValues;
      return Object.keys(now)
        .filter((key) => old[key] !== now[key])
        .reduce((obj, key) => {
          obj[key] = now[key];
          return obj;
        }, {});
    },
    get rewardsDiff() {
      const old = self.oldRewardValues;
      const now = self.rewardValues;
      if (!old.hasRewards && !now.hasRewards) return {};
      return Object.keys(now)
        .filter((key) => old[key] !== now[key])
        .reduce((obj, key) => {
          obj[key] = now[key];
          return obj;
        }, {});
    },
  }));

const JobEditStep3 = t
  .model('JobEditStep3', {})
  .actions((self) => ({
    saveStep3: flow(function* (cont) {
      if (!self.step3Dirty) {
        self.saveDraftStage(4);
        cont && self.goToEditPreview();
        return;
      }

      self.saving = true;
      const success = yield self.saveFollowers();
      if (success) {
        self.setInitialState();
        cont && self.goToEditPreview();
      }
      self.saving = false;
    }),
    saveFollowers: flow(function* () {
      const obj = {
        followers: getSnapshot(self.followers),
        appFollowers: self.appFollowers.map(({ id }) => id),
      };
      const res = yield self.env.api.jobs.followers(self.id, obj);
      if (!res.error) {
        self.original.updateLocal({
          followers: getSnapshot(self.followers),
          appFollowers: getSnapshot(self.appFollowers),
        });
        self.env.showSuccess(self.env.t('job.update.followers.success'), {
          subtitle: self.title,
        });
        return true;
      }
      self.env.showError(self.env.t('job.update.followers.error'), { subtitle: self.title });
      return false;
    }),
  }))
  .views((self) => ({
    get step3Dirty() {
      const now = self.followers;
      const old = self.original.followers;
      const oldDiff = old.filter((f) => !now.includes(f));
      const nowDiff = now.filter((f) => !old.includes(f));
      const nowApp = self.appFollowers.map(({ id }) => id);
      const oldApp = self.original.appFollowers.map(({ id }) => id);
      const oldAppDiff = oldApp.filter((f) => !nowApp.includes(f));
      const nowAppDiff = nowApp.filter((f) => !oldApp.includes(f));
      return (
        oldDiff.length !== 0 ||
        nowDiff.length !== 0 ||
        oldAppDiff.length !== 0 ||
        nowAppDiff.length !== 0
      );
    },
  }));

export const JobEdit = t
  .compose(Job, Edit, JobEditStep0, JobEditStep1, JobEditStep2, JobEditStep3)
  .named('JobEdit');
